import { blinkConstants } from '@thoughtspot/blink-constants';
import { BlinkContextOptions } from '@thoughtspot/blink-context';
import { getDaysFromNow } from '@thoughtspot/date-format-util';
import { translate } from '@thoughtspot/i18n';
import { jsonConstants } from '@thoughtspot/json-constants';
import * as session from '@thoughtspot/session-service';
import { ISessionInfo } from '@thoughtspot/session-service';
import { isBoolean } from 'lodash';
import {
    UpdateClientStatePreferenceDocument,
    UpdateClientStatePreferenceMutation,
    UpdateClientStatePreferenceMutationVariables,
    UpdateIsFirstTimeEnableVersionHistoryUserDocument,
    UpdateIsFirstTimeEnableVersionHistoryUserMutation,
    UpdateIsFirstTimeEnableVersionHistoryUserMutationVariables,
    UpdateIsFirstTimeSpotIqUserDocument,
    UpdateIsFirstTimeSpotIqUserMutation,
    UpdateIsFirstTimeSpotIqUserMutationVariables,
    UpdateIsFirstTimeVersionHistoryUserDocument,
    UpdateIsFirstTimeVersionHistoryUserMutation,
    UpdateIsFirstTimeVersionHistoryUserMutationVariables,
    UpdateIsSlackInitiallyConnectedByUserDocument,
    UpdateIsSlackInitiallyConnectedByUserMutation,
    UpdateIsSlackInitiallyConnectedByUserMutationVariables,
    UpdateVersionPreferenceDocument,
    UpdateVersionPreferenceMutation,
    UpdateVersionPreferenceMutationVariables,
} from '/@services/generated/graphql-types';
import { useMutation } from '../../../utils/error-handling/apollo-hooks-wrappers/custom-apollo-hooks';
import { flags } from './flags-service';

export function initSessionService(sessionInfo: ISessionInfo) {
    session.setSessionInfo(sessionInfo, flags);
}

export function getUserDisplayName(): string {
    const sessionInfo = session.getSessionInfo();
    return sessionInfo ? sessionInfo.userDisplayName : '';
}

export function getUserExpirationTime(): number {
    const sessionInfoObject = session.getSessionInfo();
    return sessionInfoObject ? sessionInfoObject.userExpirationTime : null;
}

export function getUserName(): string {
    const sessionInfo = session.getSessionInfo();
    return sessionInfo ? sessionInfo.userName : '';
}

export function flagMixpanelDNT(params: Record<string, string>): void {
    const queryParams = new URLSearchParams(params);
    const command = `/callosum/v1/session/mixpaneldnt?${queryParams.toString()}`;
    fetch(command);
}

/*
 * Utility for getting and setting client state preferences, with a simple setter and getter
 * interface. The preference data can be a string, boolean, or number. The state is persisted on
 * the backend, but a cached version is stored locally and managed through session-store. Both the
 * getter and setter use the locally-cached data, but the setter also triggers a network call to
 * update the data stored on the backend. Note that when calling the setter, the local data will
 * be updated immediately, so you don't need to wait for the network call.
 */
export const useClientStatePreference = () => {
    const [
        updateClientStatePreference,
        { data: setStateData, error: setStateError, loading: setStateLoading },
    ] = useMutation<
        UpdateClientStatePreferenceMutation,
        UpdateClientStatePreferenceMutationVariables
    >(UpdateClientStatePreferenceDocument);

    const getClientStatePreference = (preferenceKey: string) => {
        return session.getPreference(preferenceKey);
    };

    const setClientStatePreference = (
        preferenceKey: string,
        preferenceValue: string | boolean | number | null,
    ) => {
        // Update the local cache immediately
        session.setPreference(preferenceKey, preferenceValue, false);

        // Store the value in the correct field based on its data type
        const preferenceValueVariables: Partial<UpdateClientStatePreferenceMutationVariables> = {};
        switch (typeof preferenceValue) {
            case 'string':
                preferenceValueVariables.preferenceValueStr = preferenceValue;
                break;
            case 'boolean':
                preferenceValueVariables.preferenceValueBool = preferenceValue;
                break;
            case 'number':
                if (Number.isInteger(preferenceValue)) {
                    preferenceValueVariables.preferenceValueInt = preferenceValue;
                } else {
                    preferenceValueVariables.preferenceValueFloat = preferenceValue;
                }
                break;
            default:
                break;
        }

        // Trigger network call to update on backend
        updateClientStatePreference({
            variables: {
                preferenceKey,
                ...preferenceValueVariables,
            },
        });
    };

    return {
        setStateData: setStateData?.Session__updateClientStatePreference,
        setStateError,
        setStateLoading,
        getClientStatePreference,
        setClientStatePreference,
    };
};

export const useUpdateIsFirstTimeSpotIQUser = () => {
    const [updateIsFirstTimeSpotIQUser, { data, error, loading }] = useMutation<
        UpdateIsFirstTimeSpotIqUserMutation,
        UpdateIsFirstTimeSpotIqUserMutationVariables
    >(UpdateIsFirstTimeSpotIqUserDocument);

    return {
        data: data?.Session__updateIsFirstTimeSpotIQUser,
        error,
        loading,
        updateIsFirstTimeSpotIQUser: (isFirstTimeSpotIQUser: boolean) =>
            updateIsFirstTimeSpotIQUser({
                variables: {
                    isFirstTimeSpotIQUser,
                },
            }),
    };
};

export const useUpdateIsSlackInitiallyConnectedByUser = () => {
    const [
        updateIsSlackInitiallyConnectedByUser,
        { data, error, loading },
    ] = useMutation<
        UpdateIsSlackInitiallyConnectedByUserMutation,
        UpdateIsSlackInitiallyConnectedByUserMutationVariables
    >(UpdateIsSlackInitiallyConnectedByUserDocument);

    return {
        data: data?.Session__updateIsSlackInitiallyConnectedByUser,
        error,
        loading,
        updateIsSlackInitiallyConnectedByUser: (
            isSlackInitiallyConnectedByUser: boolean,
        ) =>
            updateIsSlackInitiallyConnectedByUser({
                variables: {
                    isSlackInitiallyConnectedByUser,
                },
            }),
    };
};

export const useUpdateIsFirstTimeVersionHistoryUser = () => {
    const [
        updateIsFirstTimeVersionHistoryUser,
        { data, error, loading },
    ] = useMutation<
        UpdateIsFirstTimeVersionHistoryUserMutation,
        UpdateIsFirstTimeVersionHistoryUserMutationVariables
    >(UpdateIsFirstTimeVersionHistoryUserDocument);

    return {
        data: data?.Session__updateIsFirstTimeVersionHistoryUser,
        error,
        loading,
        updateIsFirstTimeVersionHistoryUser: (
            isFirstTimeVersionHistoryUser: boolean,
        ) =>
            updateIsFirstTimeVersionHistoryUser({
                variables: {
                    isFirstTimeVersionHistoryUser,
                },
            }),
    };
};

export const useUpdateIsFirstTimeEnableVersionHistoryUser = () => {
    const [
        updateIsFirstTimeEnableVersionHistoryUser,
        { data, error, loading },
    ] = useMutation<
        UpdateIsFirstTimeEnableVersionHistoryUserMutation,
        UpdateIsFirstTimeEnableVersionHistoryUserMutationVariables
    >(UpdateIsFirstTimeEnableVersionHistoryUserDocument);

    return {
        data: data?.Session__updateIsFirstTimeEnableVersionHistoryUser,
        error,
        loading,
        updateIsFirstTimeEnableVersionHistoryUser: (
            isFirstTimeEnableVersionHistoryUser: boolean,
        ) =>
            updateIsFirstTimeEnableVersionHistoryUser({
                variables: {
                    isFirstTimeEnableVersionHistoryUser,
                },
            }),
    };
};

export const useUpdateNotAFirstTimeSpotIQUser = (
    useAppContext: () => BlinkContextOptions,
) => {
    const { sessionService } = useAppContext();
    const { getPreference, setPreference } = sessionService;
    const { updateIsFirstTimeSpotIQUser } = useUpdateIsFirstTimeSpotIQUser();
    let isFirstTimeSpotIQUser = getPreference(
        blinkConstants.preferenceKeys.IS_FIRST_TIME_SPOT_IQ_USER,
    );
    isFirstTimeSpotIQUser =
        !isBoolean(isFirstTimeSpotIQUser) || isFirstTimeSpotIQUser;

    const setAsNotAFirstTimeSpotIQUser = () => {
        setPreference(
            blinkConstants.preferenceKeys.IS_FIRST_TIME_SPOT_IQ_USER,
            false,
        );
        updateIsFirstTimeSpotIQUser(false);
    };
    return { isFirstTimeSpotIQUser, setAsNotAFirstTimeSpotIQUser };
};

export const useUpdateNotAFirstTimeVersionHistoryUser = (
    useAppContext: () => BlinkContextOptions,
) => {
    const { sessionService } = useAppContext();
    const { getPreference, setPreference } = sessionService;
    const {
        updateIsFirstTimeVersionHistoryUser,
    } = useUpdateIsFirstTimeVersionHistoryUser();
    let isFirstTimeVersionHistoryUser = getPreference(
        blinkConstants.preferenceKeys.IS_FIRST_TIME_VERSION_HISTORY_USER,
    );
    isFirstTimeVersionHistoryUser =
        !isBoolean(isFirstTimeVersionHistoryUser) ||
        isFirstTimeVersionHistoryUser;

    const setAsNotAFirstTimeVersionHistoryUser = () => {
        setPreference(
            blinkConstants.preferenceKeys.IS_FIRST_TIME_VERSION_HISTORY_USER,
            false,
        );
        updateIsFirstTimeVersionHistoryUser(false);
    };
    return {
        isFirstTimeVersionHistoryUser,
        setAsNotAFirstTimeVersionHistoryUser,
    };
};

export const useUpdateNotAFirstTimeEnableVersionHistoryUser = (
    useAppContext: () => BlinkContextOptions,
) => {
    const { sessionService } = useAppContext();
    const { getPreference, setPreference } = sessionService;
    const {
        updateIsFirstTimeEnableVersionHistoryUser,
    } = useUpdateIsFirstTimeEnableVersionHistoryUser();
    let isFirstTimeEnableVersionHistoryUser = getPreference(
        blinkConstants.preferenceKeys.IS_FIRST_TIME_ENABLE_VERSION_HISTORY_USER,
    );
    isFirstTimeEnableVersionHistoryUser =
        !isBoolean(isFirstTimeEnableVersionHistoryUser) ||
        isFirstTimeEnableVersionHistoryUser;

    const setAsNotAFirstTimeEnableVersionHistoryUser = () => {
        setPreference(
            blinkConstants.preferenceKeys
                .IS_FIRST_TIME_ENABLE_VERSION_HISTORY_USER,
            false,
        );
        updateIsFirstTimeEnableVersionHistoryUser(false);
    };
    return {
        isFirstTimeEnableVersionHistoryUser,
        setAsNotAFirstTimeEnableVersionHistoryUser,
    };
};

export const useUpdateVersionPreferenceMutation = () => {
    const [
        updateVersionPreferenceMutation,
        { data, error, loading },
    ] = useMutation<
        UpdateVersionPreferenceMutation,
        UpdateVersionPreferenceMutationVariables
    >(UpdateVersionPreferenceDocument);

    return {
        data: data?.Session__updateVersionPreference,
        error,
        loading,
        updateVersionPreference: (
            isAnswerV2Preferred: boolean,
            isSpotIQV2Preferred: boolean,
            isDataPanelV2Preferred: boolean,
            isModularHomeExperiencePreferred: boolean,
            isConvAssistExperiencePreferred: boolean,
            isKPIAnomaliesPrefered: boolean,
            chartContextMenuExperiencePreferred: string,
            isSpotterAgentPreferred: boolean,
        ) =>
            updateVersionPreferenceMutation({
                variables: {
                    isAnswerV2Preferred,
                    isSpotIQV2Preferred,
                    isDataPanelV2Preferred,
                    isModularHomeExperiencePreferred,
                    isConvAssistExperiencePreferred,
                    isKPIAnomaliesPrefered,
                    chartContextMenuExperiencePreferred,
                    isSpotterAgentPreferred,
                },
            }),
    };
};

export const getProductName = (): string => {
    const productName = session.getProductNameWrapper();
    return productName === session.DEFAULT_PRODUCT_NAME
        ? translate('THOUGHTSPOT')
        : productName;
};

export const getCanChangePassword = function() {
    const sessionInfo = session.getSessionInfo();
    if (!sessionInfo) {
        return false;
    }
    return sessionInfo[jsonConstants.CAN_CHANGE_PASSWORD as keyof ISessionInfo];
};

export const trialDaysLeft = (): number => {
    const expiryTimestamp: number = session.trialDaysLeftWrapper();
    return expiryTimestamp > 0
        ? Number(getDaysFromNow(expiryTimestamp))
        : expiryTimestamp;
};

/* Do not remove this canShowTeamsTab function - it's actively used in all MY* production clusters.
 * This function is used to check if the teams tab should be shown to the user.
 * SREs should not see TEAMS ADMIN and all other free trial and teams edition user should.
 * We dont want to show both TEAMS_ADMIN TAB and ADMIN TAB together hence session.isAdminTabVisible() condition is added.
 * !session.isOrgsOnFTEnabled() is added to make sure it is not seen in org enabled clusters.
 * We check if isTeamOwner is set to true or not and inside team admin page we decide what is to be shown in the left nav.
 */
export const canShowTeamsTab: () => boolean = () => {
    return (
        session.enableTeamsAdminTab() &&
        !session.isAdminTabVisible() &&
        !session.isOrgsOnFTEnabled() &&
        session.isTeamsEnabled() &&
        (session.isTeamsOrEssentialsEdition() ||
            session.getSubscriptionType() ===
                session.SubscriptionType.FREE_TRIAL)
    );
};

export * from '@thoughtspot/session-service';
